<script setup lang="ts">
import { CompanyInfoSelect, FormSchema } from '@/@types/participant'
import { Input } from '@/components/ui/input'
import Label from '@/components/ui/label/Label.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ref } from 'vue'

const { company_info_select } = defineProps<{ form: FormSchema; errors: Record<string, string>; company_info_select: CompanyInfoSelect }>()

const areas = ref(company_info_select.areas)
const sectors = ref(company_info_select.sectors)
const hierarchical_levels = ref(company_info_select.occupations)
</script>

<template>
    <fieldset class="grid gap-4 gap-x-8 lg:grid-cols-2">
        <legend class="mb-4 font-sans text-lg font-semibold text-gray-800">
            {{ $t('Professional information') }}
        </legend>

        <div class="flex flex-col gap-1.5">
            <Label required for="occupation">{{ $t('Occupation') }}</Label>
            <Input type="text" id="occupation" v-model="form.company_attributes.occupation" :placeholder="$t('Occupation')" />
            <p v-if="errors.company_occupation" class="text-destructive text-xs">
                {{ errors.company_occupation }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label required for="hierarchical_level">{{ $t('Hierarchical level') }}</Label>
            <Select v-model="form.company_attributes.hierarchical_level">
                <SelectTrigger class="w-full">
                    <SelectValue :placeholder="$t('Select')" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="hierarchical_level in hierarchical_levels" :key="hierarchical_level.slug" :value="hierarchical_level.slug">
                        <template v-if="$i18n.locale === 'pt-BR'">
                            {{ hierarchical_level.name }}
                        </template>
                        <template v-else>
                            {{ hierarchical_level.name_en ?? hierarchical_level.name }}
                        </template>
                    </SelectItem>
                </SelectContent>
            </Select>
            <p v-if="errors.company_hierarchical_level" class="text-destructive text-xs">
                {{ errors.company_hierarchical_level }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label required for="area">{{ $t('Area') }}</Label>
            <Select v-model="form.company_attributes.area">
                <SelectTrigger class="w-full">
                    <SelectValue :placeholder="$t('Select')" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="area in areas" :key="area.slug" :value="area.slug">
                        <template v-if="$i18n.locale === 'pt-BR'">
                            {{ area.name }}
                        </template>
                        <template v-else>
                            {{ area.name_en ?? area.name }}
                        </template>
                    </SelectItem>
                </SelectContent>
            </Select>
            <p v-if="errors.company_area" class="text-destructive text-xs">
                {{ errors.company_area }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label required for="name">{{ $t('Company') }}</Label>
            <Input type="text" id="name" v-model="form.company_attributes.name" :placeholder="$t('Company')" />
            <p v-if="errors.company_name" class="text-destructive text-xs">
                {{ errors.company_name }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label required for="sector">{{ $t('Sector') }}</Label>
            <Select v-model="form.company_attributes.sector">
                <SelectTrigger class="w-full">
                    <SelectValue :placeholder="$t('Select')" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="sector in sectors" :key="sector.slug" :value="sector.slug">
                        <template v-if="$i18n.locale === 'pt-BR'">
                            {{ sector.name }}
                        </template>
                        <template v-else>
                            {{ sector.name_en ?? sector.name }}
                        </template>
                    </SelectItem>
                </SelectContent>
            </Select>
            <p v-if="errors.company_sector" class="text-destructive text-xs">
                {{ errors.company_sector }}
            </p>
        </div>
    </fieldset>
</template>
